"use client";

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { PatientSelectorModalProps, Patient } from "./types";
import { defaultModalTheme } from "./themes";
import { mergeTheme } from "../utils/theme";
import { PatientSelector } from "./patient-selector";

/**
 * PatientSelectorModal Component
 *
 * A modal dialog for selecting patients with customizable layouts, theming,
 * and behavior options. Supports grid and list layouts, auto-confirmation,
 * custom rendering functions, and accessibility features including keyboard
 * navigation and screen reader support.
 *
 * Features:
 * - Portal-based rendering for proper z-index layering
 * - Body scroll prevention when open
 * - Keyboard navigation (Escape to close)
 * - Click-outside-to-close functionality
 * - Auto-confirmation on selection option
 * - Customizable header and footer rendering
 * - Loading and disabled states
 * - Analytics event tracking
 *
 * @param isOpen - Controls modal visibility
 * @param onClose - Called when modal should close
 * @param patients - Array of patient objects to display
 * @param initialSelectedPatient - Initially selected patient
 * @param onConfirm - Called when user confirms selection
 * @param onCancel - Called when user cancels (optional, defaults to onClose)
 * @param title - Modal title text
 * @param subtitle - Modal subtitle text
 * @param confirmText - Confirm button text (can be function or string)
 * @param cancelText - Cancel button text
 * @param showCloseButton - Whether to show X close button
 * @param autoConfirmOnSelect - Whether to auto-confirm when patient selected
 * @param theme - Modal theme overrides
 * @param selectorTheme - Patient selector theme overrides
 * @param selectorLayout - Layout type for patient selector
 * @param showChevron - Whether to show chevron indicators
 * @param className - Additional CSS classes for modal
 * @param renderHeader - Custom header render function
 * @param renderFooter - Custom footer render function
 * @param selectionIndicator - Custom selection indicator component
 * @param renderAvatar - Custom avatar render function
 * @param renderLabel - Custom label render function
 * @param renderChevron - Custom chevron render function
 * @param avatarBaseUrl - Base URL for avatar images
 * @param loading - Whether modal is in loading state
 * @param disabled - Whether interactions are disabled
 * @param eventData - Analytics event data
 * @param customEventName - Custom analytics event name
 * @param ariaLabel - Accessibility label for modal
 * @param data-testid - Test identifier
 */
export const PatientSelectorModal: React.FC<PatientSelectorModalProps> = ({
  isOpen,
  onClose,
  patients,
  initialSelectedPatient,
  onConfirm,
  onCancel,
  title = "Who is this visit for?",
  subtitle = "Select the person who needs the visit",
  confirmText,
  showCloseButton = true,
  autoConfirmOnSelect = false,
  theme,
  selectorTheme,
  selectorLayout = "grid",
  showChevron = false,
  className = "",
  renderHeader,
  renderFooter,
  selectionIndicator,
  renderAvatar,
  renderLabel,
  renderChevron,
  avatarBaseUrl,
  loading = false,
  disabled = false,
  ariaLabel = "Patient selection modal",
  "data-testid": dataTestId,
}) => {
  const [selectedPatient, setSelectedPatient] = useState(
    initialSelectedPatient || patients[0] || null
  );
  const [mounted, setMounted] = useState(false);

  const mergedTheme = mergeTheme(defaultModalTheme, theme);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    if (selectedPatient && !disabled && !loading) {
      onConfirm(selectedPatient);
    }
  }, [selectedPatient, disabled, loading, onConfirm]);

  // Handle patient selection - can optionally auto-confirm
  const handlePatientSelect = useCallback(
    (patient: Patient) => {
      setSelectedPatient(patient);
      // Auto-confirm on selection if enabled
      if (autoConfirmOnSelect) {
        onConfirm(patient);
      }
    },
    [onConfirm, autoConfirmOnSelect]
  );

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  }, [onCancel, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleCancel]
  );

  if (!mounted || !isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className={mergedTheme.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      <div
        className={`${mergedTheme.modal} ${className}`.trim()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {renderHeader ? (
          renderHeader()
        ) : (
          <div className={`${mergedTheme.header} relative`}>
            {showCloseButton && (
              <button
                onClick={handleCancel}
                className="absolute top-0 right-0 bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-current opacity-70 transition-opacity duration-200 hover:opacity-100"
                aria-label="Close modal"
                data-testid="close-button"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <h2 className={mergedTheme.title}>{title}</h2>
            {subtitle && <p className={mergedTheme.subtitle}>{subtitle}</p>}
          </div>
        )}

        {/* Body */}
        <div className={mergedTheme.body}>
          <PatientSelector
            patients={patients}
            selectedPatient={selectedPatient}
            onSelectPatient={handlePatientSelect}
            theme={selectorTheme}
            layout={selectorLayout}
            showChevron={showChevron}
            selectionIndicator={selectionIndicator}
            renderAvatar={renderAvatar}
            renderLabel={renderLabel}
            renderChevron={renderChevron}
            avatarBaseUrl={avatarBaseUrl}
          />
        </div>

        {/* Footer */}
        {renderFooter &&
          renderFooter(selectedPatient, handleConfirm, handleCancel)}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

PatientSelectorModal.displayName = "PatientSelectorModal";
